/**
 * Nara orb — loading (site default) plus voice-mode shapes.
 * Modes: loading | anneaux | thinking | processing | speaking
 * Gold #D39542 on black. Transitions lerp; no hard cuts.
 */

const PARTICLE_COUNT = 2800;
const CHROME_PARTICLE_SCALE = 0.4;
const TRAIL_FADE = 0.42;
const TRANSITION_MS = 480;
const MOOD_TRANSITION_MS = 520;
const IMPULSE_MS = 320;
const BLOOM_SEC = 1.2;
const GOLD = '#D39542';
const GOLD_SOFT = '#F0C56A';
const HOME_MOOD = 'DEFAULT';
const HOME_CYCLE_MOODS = Object.freeze([
  'CALM',
  'ENERGY',
  'SUCCESS',
  'STRICT',
  'MINDSET',
]);
const SPEAKING = {
  expand: 0.48,
  sizeShrink: 0.32,
  glowBase: 0.82,
  glowAmp: 0.12,
  driftAmp: 0.14,
  varMin: 0.3,
  varRange: 0.7,
  breathAmp: 0.16,
  breathFreq: 1.85,
  retractGlowCut: 0.2,
  retractAlphaCut: 0.16,
  orbitMult: 2.3,
};
const FALLBACK_MOODS = Object.freeze({
  DEFAULT: {
    gold: GOLD,
    goldSoft: GOLD_SOFT,
    glowColor: '#E2C17E',
    glow: 1,
    breatheAmt: 1,
    breatheFreq: 1,
    rotation: 1,
    turbulence: 1,
    scatter: 1,
    energy: 0.55,
    duration: MOOD_TRANSITION_MS,
  },
  STRICT: {
    gold: '#8B2E2E',
    goldSoft: '#C45C5C',
    glowColor: '#A33B3B',
    glow: 0.88,
    breatheAmt: 0.82,
    breatheFreq: 1.08,
    rotation: 0.92,
    turbulence: 0.78,
    scatter: 0.86,
    energy: 0.62,
    duration: 478,
  },
  CALM: {
    gold: '#8EC4D4',
    goldSoft: '#C5E4EC',
    glowColor: '#A9D7E2',
    glow: 0.78,
    breatheAmt: 0.72,
    breatheFreq: 0.68,
    rotation: 0.62,
    turbulence: 0.7,
    scatter: 0.74,
    energy: 0.32,
    duration: 614,
  },
  ENERGY: {
    gold: '#E07A2F',
    goldSoft: '#F0A35A',
    glowColor: '#FFB45A',
    glow: 1.18,
    breatheAmt: 1.22,
    breatheFreq: 1.28,
    rotation: 1.24,
    turbulence: 1.12,
    scatter: 1.08,
    energy: 0.88,
    duration: 458,
  },
  HEALTH: {
    gold: '#4F8F5B',
    goldSoft: '#8FBF96',
    glowColor: '#6EAE78',
    glow: 0.92,
    breatheAmt: 0.9,
    breatheFreq: 0.85,
    rotation: 0.8,
    turbulence: 0.82,
    scatter: 0.84,
    energy: 0.48,
    duration: 551,
  },
  MINDSET: {
    gold: '#B07AA8',
    goldSoft: '#D2A8CC',
    glowColor: '#C492BC',
    glow: 1.02,
    breatheAmt: 0.95,
    breatheFreq: 0.9,
    rotation: 0.88,
    turbulence: 0.9,
    scatter: 0.92,
    energy: 0.52,
    duration: 530,
  },
  SUCCESS: {
    gold: '#E8C15A',
    goldSoft: '#F6E0A0',
    glowColor: '#F0D37A',
    glow: 1.22,
    breatheAmt: 1.08,
    breatheFreq: 1.05,
    rotation: 1.06,
    turbulence: 1.02,
    scatter: 1,
    energy: 0.78,
    duration: 468,
  },
  MEDITATION: {
    gold: '#B8A4D4',
    goldSoft: '#E2C17E',
    glowColor: '#C9B6E0',
    glow: 0.7,
    breatheAmt: 0.62,
    breatheFreq: 0.52,
    rotation: 0.48,
    turbulence: 0.52,
    scatter: 0.58,
    energy: 0.22,
    duration: 666,
  },
});

function isBlinkChrome() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/CriOS|FxiOS/.test(ua)) return false;
  return /Chrome|Chromium|Edg|OPR/.test(ua);
}

const MODE_PRESETS = {
  loading: {
    sphere: 1,
    rings: 0,
    nest: 0,
    cloud: 0,
    radius: 0.9,
    rotation: 0.042,
    breatheAmt: 0.09,
    breatheFreq: 3.2,
    turbulence: 0.09,
    glow: 1.22,
    scatter: 0.06,
    trail: 0.42,
  },
  anneaux: {
    sphere: 0,
    rings: 1,
    nest: 0,
    cloud: 0,
    radius: 0.74,
    rotation: 0.016,
    breatheAmt: 0.11,
    breatheFreq: 2.1,
    turbulence: 0.018,
    glow: 1.38,
    scatter: 0.012,
    trail: 0.58,
  },
  thinking: {
    sphere: 1,
    rings: 0,
    nest: 0.08,
    cloud: 0,
    radius: 0.38,
    rotation: 0.014,
    breatheAmt: 0.045,
    breatheFreq: 1.7,
    turbulence: 0.03,
    glow: 0.92,
    scatter: 0.012,
    trail: 0.32,
  },
  processing: {
    sphere: 0.28,
    rings: 0,
    nest: 1,
    cloud: 0.12,
    radius: 0.8,
    rotation: 0.118,
    breatheAmt: 0.2,
    breatheFreq: 2.8,
    turbulence: 0.24,
    glow: 1.42,
    scatter: 0.14,
    trail: 0.26,
  },
  speaking: {
    sphere: 1,
    rings: 0,
    nest: 0,
    cloud: 1,
    radius: 0.82,
    rotation: 0.026,
    breatheAmt: 0.16,
    breatheFreq: 1.85,
    turbulence: 0.055,
    glow: 1.08,
    scatter: 0.09,
    trail: 0.36,
  },
};

function clamp01(value) {
  return Math.min(1, Math.max(0, value));
}

function lerp(from, to, amount) {
  return from + (to - from) * amount;
}

function easeInOut(amount) {
  return amount < 0.5 ? 2 * amount * amount : 1 - (-2 * amount + 2) ** 2 / 2;
}

function parseGold(value, fallback) {
  const text = String(value || '').trim();
  return /^#[0-9a-fA-F]{6}$/.test(text) ? text : fallback;
}

function hexToRgb(hex) {
  const text = parseGold(hex, GOLD).slice(1);
  return {
    r: parseInt(text.slice(0, 2), 16),
    g: parseInt(text.slice(2, 4), 16),
    b: parseInt(text.slice(4, 6), 16),
  };
}

function rgbToHex(r, g, b) {
  return `#${[r, g, b]
    .map((channel) => Math.round(Math.min(255, Math.max(0, channel))).toString(16).padStart(2, '0'))
    .join('')}`;
}

function lerpHex(from, to, amount) {
  const a = hexToRgb(from);
  const b = hexToRgb(to);
  return rgbToHex(lerp(a.r, b.r, amount), lerp(a.g, b.g, amount), lerp(a.b, b.b, amount));
}

function moodFromPersonality(id) {
  const key = String(id || HOME_MOOD).toUpperCase();
  const fallback = FALLBACK_MOODS[key] || FALLBACK_MOODS.DEFAULT;
  const personality = window.NARA_ORB_MOODS?.getPersonality?.(key);
  if (!personality) return { ...fallback };
  return {
    gold: personality.color || fallback.gold,
    goldSoft: personality.secondaryColor || fallback.goldSoft,
    glowColor: personality.glowColor || fallback.glowColor,
    glow: personality.glowIntensity ?? fallback.glow,
    breatheAmt: personality.breathAmplitude ?? fallback.breatheAmt,
    breatheFreq: personality.breathFrequency ?? fallback.breatheFreq,
    rotation: personality.rotationSpeed ?? fallback.rotation,
    turbulence: personality.turbulence ?? fallback.turbulence,
    scatter: personality.noiseStrength ?? fallback.scatter,
    energy: personality.globalEnergy ?? fallback.energy ?? 0.55,
    duration: Math.round(MOOD_TRANSITION_MS * (personality.transitionDuration || 1)),
  };
}

function demoSpeakAmp(t) {
  const talking = t % 6 < 3.5 ? 1 : 0;
  return (
    talking *
    (0.3 +
      0.42 * Math.abs(Math.sin(t * 4.1)) +
      0.22 * Math.abs(Math.sin(t * 9.3)) +
      0.1 * Math.abs(Math.sin(t * 14.7)))
  );
}

class NaraOrbLoading extends HTMLElement {
  connectedCallback() {
    if (this.canvas) return;

    const canvas = this.querySelector('canvas');
    if (!(canvas instanceof HTMLCanvasElement)) return;

    this.transparent = this.hasAttribute('data-transparent');
    this.phaseGlow = this.hasAttribute('data-phase-glow');
    this.gold = parseGold(this.getAttribute('data-gold'), GOLD);
    this.goldSoft = parseGold(this.getAttribute('data-gold-soft'), GOLD_SOFT);
    this.crisp = this.hasAttribute('data-crisp');
    const requested = Number(this.getAttribute('data-particles'));
    let particleCount =
      Number.isFinite(requested) && requested > 0
        ? Math.min(Math.round(requested), 12000)
        : this.phaseGlow
          ? 7200
          : PARTICLE_COUNT;
    if (isBlinkChrome() && !this.phaseGlow) {
      particleCount = Math.max(220, Math.round(particleCount * CHROME_PARTICLE_SCALE));
    }
    this.particleCount = particleCount;
    const requestedDots = Number(this.getAttribute('data-dot-scale'));
    this.dotScale =
      Number.isFinite(requestedDots) && requestedDots > 0 ? requestedDots : 1;
    const requestedSize = Number(this.getAttribute('data-size'));
    this.fixedSize =
      Number.isFinite(requestedSize) && requestedSize > 1 ? requestedSize : 0;
    const context = canvas.getContext('2d', { alpha: this.transparent });
    if (!context) return;

    this.canvas = canvas;
    this.context = context;
    this.particles = [];
    this.ringParticles = [];
    this.time = 0;
    this.lastFrame = 0;
    this.frameId = 0;
    this.mode = this.getAttribute('data-mode') || 'loading';
    this.fromPreset = { ...MODE_PRESETS[this.mode] };
    this.toPreset = { ...MODE_PRESETS[this.mode] };
    this.transitionStart = 0;
    this.moodId = HOME_MOOD;
    this.atHome = true;
    this.fromMood = moodFromPersonality(HOME_MOOD);
    this.toMood = { ...this.fromMood };
    this.moodTransitionStart = 0;
    this.moodDuration = MOOD_TRANSITION_MS;
    this.guestIndex = 0;
    this.impulseStart = 0;
    this.lastTapAt = 0;
    this.moodAbort = new AbortController();
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const bloomIn = this.hasAttribute('data-bloom-in') && !this.reducedMotion.matches;
    this.bloom = bloomIn ? 0 : 1;
    this.bloomArmed = !bloomIn;

    this.inView = this.hasAttribute('data-always-run');
    this.onResize = () => this.resize();
    this.onMotionChange = () => this.start();
    this.onVisibility = () => {
      if (this.isActive()) this.start();
      else this.stop();
    };
    this.onIntersect = (entries) => {
      if (this.hasAttribute('data-always-run') || this.closest('dialog')) {
        this.inView = true;
        this.start();
        return;
      }
      const entry = entries[0];
      this.inView = Boolean(entry?.isIntersecting);
      if (this.isActive()) this.start();
      else this.stop();
    };

    window.addEventListener('resize', this.onResize);
    this.reducedMotion.addEventListener('change', this.onMotionChange);
    document.addEventListener('visibilitychange', this.onVisibility);
    this.io = new IntersectionObserver(this.onIntersect, {
      rootMargin: '12% 0px',
      threshold: 0.01,
    });
    this.io.observe(this);
    this.ro = new ResizeObserver(() => {
      if (this.closest('dialog')) return;
      this.resize();
      if (this.isActive() && !this.frameId) this.start();
    });
    this.ro.observe(this);

    this.resize();
    this.initParticles();
    this.bindMoodTap();
    this.syncHalo(this.toMood);
    if (this.isActive()) this.start();
    else this.frame(performance.now());
  }

  playBloom() {
    if (this.reducedMotion?.matches) {
      this.bloom = 1;
      this.bloomArmed = true;
      return;
    }
    this.bloom = 0;
    this.bloomArmed = true;
  }

  bloomEase(x) {
    const t = Math.max(0, Math.min(1, x));
    return 1 - (1 - t) ** 3;
  }

  isActive() {
    if (document.hidden) return false;
    if (this.hasAttribute('data-always-run')) return true;
    if (this.closest('dialog')?.open) return true;
    return this.inView;
  }

  disconnectedCallback() {
    this.stop();
    this.io?.disconnect();
    this.ro?.disconnect();
    this.moodAbort?.abort();
    window.removeEventListener('resize', this.onResize);
    this.reducedMotion?.removeEventListener('change', this.onMotionChange);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.canvas = undefined;
  }

  bindMoodTap() {
    if (this.getAttribute('data-mood-tap') !== 'home-cycle') return;
    const { signal } = this.moodAbort;
    let startX = 0;
    let startY = 0;

    this.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        startX = event.clientX;
        startY = event.clientY;
      },
      { signal }
    );
    this.addEventListener(
      'pointerup',
      (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        const dx = event.clientX - startX;
        const dy = event.clientY - startY;
        if (Math.hypot(dx, dy) > 12) return;
        this.cycleMoodTap();
      },
      { signal }
    );
    this.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        this.cycleMoodTap();
      },
      { signal }
    );
  }

  cycleMoodTap() {
    const now = performance.now();
    if (now - this.lastTapAt < 280) return;
    this.lastTapAt = now;
    const guests = window.NARA_ORB_MOODS?.NARA_HOME_CYCLE_MOODS || HOME_CYCLE_MOODS;
    if (!this.atHome) {
      this.atHome = true;
      this.setMood(HOME_MOOD);
      this.setMode('loading');
      return;
    }
    const next = guests[this.guestIndex % guests.length] || 'CALM';
    this.guestIndex = (this.guestIndex + 1) % guests.length;
    this.atHome = false;
    this.setMood(next);
    this.setMode('speaking');
  }

  setMood(id) {
    const next = String(id || HOME_MOOD).toUpperCase();
    const now = performance.now();
    const target = moodFromPersonality(next);
    this.fromMood = this.currentMood(now);
    this.toMood = target;
    this.moodId = next;
    this.moodDuration = this.reducedMotion?.matches ? 1 : Math.max(280, target.duration || MOOD_TRANSITION_MS);
    this.moodTransitionStart = now;
    this.setAttribute('data-mood', next);
    this.bounce();
    if (!this.frameId) this.start();
  }

  currentMood(now) {
    if (!this.moodTransitionStart) return this.toMood;
    const duration = this.moodDuration || MOOD_TRANSITION_MS;
    const raw = clamp01((now - this.moodTransitionStart) / duration);
    const amount = this.reducedMotion?.matches ? 1 : easeInOut(raw);
    if (raw >= 1) {
      this.moodTransitionStart = 0;
      this.fromMood = this.toMood;
      this.syncHalo(this.toMood);
      return this.toMood;
    }
    const from = this.fromMood;
    const to = this.toMood;
    const mixed = {
      gold: lerpHex(from.gold, to.gold, amount),
      goldSoft: lerpHex(from.goldSoft, to.goldSoft, amount),
      glowColor: lerpHex(from.glowColor, to.glowColor, amount),
      glow: lerp(from.glow, to.glow, amount),
      breatheAmt: lerp(from.breatheAmt, to.breatheAmt, amount),
      breatheFreq: lerp(from.breatheFreq, to.breatheFreq, amount),
      rotation: lerp(from.rotation, to.rotation, amount),
      turbulence: lerp(from.turbulence, to.turbulence, amount),
      scatter: lerp(from.scatter, to.scatter, amount),
      energy: lerp(from.energy ?? 0.55, to.energy ?? 0.55, amount),
      duration: to.duration,
    };
    this.syncHalo(mixed);
    return mixed;
  }

  bounce() {
    if (this.reducedMotion?.matches) return;
    this.impulseStart = performance.now();
    this.classList.remove('is-bounce');
    void this.offsetWidth;
    this.classList.add('is-bounce');
    window.setTimeout(() => this.classList.remove('is-bounce'), IMPULSE_MS + 40);
  }

  currentImpulse(now) {
    if (!this.impulseStart || this.reducedMotion?.matches) return 1;
    const t = (now - this.impulseStart) / IMPULSE_MS;
    if (t >= 1) {
      this.impulseStart = 0;
      return 1;
    }
    if (t < 0.28) return lerp(1, 1.07, 1 - (1 - t / 0.28) ** 2);
    return lerp(1.07, 1, easeInOut((t - 0.28) / 0.72));
  }

  syncHalo(mood) {
    const host = this.closest('.nara-landing-meaning__orb-col');
    if (!host || !mood) return;
    const gold = hexToRgb(mood.gold);
    const soft = hexToRgb(mood.glowColor || mood.goldSoft);
    host.style.setProperty('--nara-meaning-glow', `${gold.r} ${gold.g} ${gold.b}`);
    host.style.setProperty('--nara-meaning-glow-soft', `${soft.r} ${soft.g} ${soft.b}`);
  }

  setMode(mode) {
    const next = MODE_PRESETS[mode] ? mode : 'loading';
    if (next === this.mode && this.transitionStart === 0) return;
    this.fromPreset = this.currentPreset(performance.now());
    this.toPreset = { ...MODE_PRESETS[next] };
    this.mode = next;
    this.transitionStart = performance.now();
    this.setAttribute('data-mode', next);
  }

  currentPreset(now) {
    if (!this.transitionStart) return this.toPreset;
    const raw = clamp01((now - this.transitionStart) / TRANSITION_MS);
    const amount = easeInOut(raw);
    if (raw >= 1) {
      this.transitionStart = 0;
      this.fromPreset = this.toPreset;
      return this.toPreset;
    }
    const mixed = {};
    for (const key of Object.keys(this.toPreset)) {
      mixed[key] = lerp(this.fromPreset[key] ?? 0, this.toPreset[key] ?? 0, amount);
    }
    return mixed;
  }

  resize() {
    const canvas = this.canvas;
    const context = this.context;
    if (!canvas || !context) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const css = window.getComputedStyle(this);
    const cssW = parseFloat(css.width);
    const cssH = parseFloat(css.height);
    let width = canvas.clientWidth || this.offsetWidth || 0;
    let height = canvas.clientHeight || this.offsetHeight || 0;
    if (width < 2 && Number.isFinite(cssW) && cssW > 2) width = cssW;
    if (height < 2 && Number.isFinite(cssH) && cssH > 2) height = cssH;
    if (width < 2 && this.fixedSize) width = this.fixedSize;
    if (height < 2 && this.fixedSize) height = this.fixedSize;
    if (width < 2) width = 1;
    if (height < 2) height = 1;
    const nextW = Math.round(width * dpr);
    const nextH = Math.round(height * dpr);
    if (
      this.drawWidth === width &&
      this.drawHeight === height &&
      canvas.width === nextW &&
      canvas.height === nextH
    ) {
      return;
    }
    this.drawWidth = width;
    this.drawHeight = height;
    canvas.width = nextW;
    canvas.height = nextH;
    context.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  initParticles() {
    this.particles = [];
    this.ringParticles = [];

    for (let index = 0; index < this.particleCount; index += 1) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      this.particles.push({
        theta,
        phi,
        baseR: 0.78 + Math.random() * 0.22,
        cloudR: 0.58 + Math.random() * 0.42,
        cloudOmega: 0.002 + Math.random() * 0.016,
        explodeVar: SPEAKING.varMin + Math.random() * SPEAKING.varRange,
        seedX: Math.random(),
        seedY: Math.random(),
        speed: 0.0035 + Math.random() * 0.01,
        size: (0.55 + Math.random() * 0.45) * (this.phaseGlow ? 1.28 : 1),
        twinkle: Math.random() * Math.PI * 2,
        layer: Math.random(),
        dir: Math.random() > 0.5 ? 1 : -1,
      });
    }
    this.particles.sort((a, b) => a.layer - b.layer);

    const ringCount = 5;
    for (let ring = 0; ring < ringCount; ring += 1) {
      const count = 90 + ring * 28;
      for (let index = 0; index < count; index += 1) {
        this.ringParticles.push({
          ring,
          angle: (index / count) * Math.PI * 2,
          jitter: (Math.random() - 0.5) * 0.018,
          size: 0.7 + Math.random() * 0.55,
          twinkle: Math.random() * Math.PI * 2,
          dir: ring % 2 === 0 ? 1 : -1,
        });
      }
    }
  }

  liveParams(preset, time, mood) {
    const breatheAmt = preset.breatheAmt * (mood?.breatheAmt ?? 1);
    const breatheFreq = preset.breatheFreq * (mood?.breatheFreq ?? 1);
    const rotation = preset.rotation * (mood?.rotation ?? 1);
    const turbulence = preset.turbulence * (mood?.turbulence ?? 1);
    const glow = preset.glow * (mood?.glow ?? 1);
    const scatter = preset.scatter * (mood?.scatter ?? 1);
    return {
      ...preset,
      rotation: rotation + Math.sin(time * 1.1) * 0.008,
      breathe: 1 + Math.sin(time * breatheFreq) * breatheAmt,
      turbulence: turbulence + Math.sin(time * 2) * turbulence * 0.35,
      glow: glow + Math.sin(time * 4) * 0.06,
      scatter: scatter + Math.sin(time * 1.5) * scatter * 0.25,
    };
  }

  speakingPos(particle, time, audio, mood) {
    const breathAmp = SPEAKING.breathAmp * (mood?.breatheAmt ?? 1);
    const breathFreq = SPEAKING.breathFreq * (mood?.breatheFreq ?? 1);
    const spin = mood?.rotation ?? 1;
    const breathWave = Math.sin(time * breathFreq) * breathAmp * (1 + audio * 0.5);
    const breath = 1 + breathWave;
    const retract = breathAmp > 0 ? Math.max(0, -breathWave / breathAmp) : 0;
    const pull = 0.55 + audio * 0.4;

    let theta = particle.theta + time * particle.cloudOmega * particle.dir * SPEAKING.orbitMult * spin;
    let phi = particle.phi + Math.sin(time * 0.7 + particle.seedY) * 0.04;
    let r = particle.cloudR * (1 + Math.sin(time * 2.6 + particle.seedX * 6) * 0.05);
    r *= 1 - pull * 0.35;

    const sPhi = Math.sin(phi);
    let x = r * sPhi * Math.cos(theta);
    let y = r * sPhi * Math.sin(theta);
    let z = r * Math.cos(phi);
    const expand = breath * (1 + audio * SPEAKING.expand * particle.explodeVar);
    x *= expand;
    y *= expand;
    z *= expand;

    const size = particle.size * (1 - audio * SPEAKING.sizeShrink * particle.explodeVar);
    let glow = (SPEAKING.glowBase + audio * SPEAKING.glowAmp) * (mood?.glow ?? 1);
    glow *= 1 - retract * SPEAKING.retractGlowCut;
    let alpha = (0.62 + (1 - r) * 0.18) * (0.7 + audio * 0.28);
    alpha *= 1 - retract * SPEAKING.retractAlphaCut;
    return { x, y, z, size, alpha, glow };
  }

  drawSphere(centerX, centerY, baseRadius, params, time, weight, mood, audio) {
    if (weight < 0.02) return;
    const context = this.context;
    const rotY = time * params.rotation * 60;
    const cosR = Math.cos(rotY);
    const sinR = Math.sin(rotY);
    const turb = 1 + params.turbulence * 4;
    const nest = params.nest || 0;
    const cloud = clamp01(params.cloud || 0);

    const gold = this.liveGold || this.gold || GOLD;
    const goldSoft = this.liveGoldSoft || this.goldSoft || GOLD_SOFT;
    const crisp = this.crisp ? 1.18 : 1;
    const phaseGlow = this.phaseGlow ? 1.55 : 1;
    const fillGold = this.phaseGlow ? lerpHex(gold, '#FFE2A3', 0.28) : gold;
    context.fillStyle = fillGold;
    for (const particle of this.particles) {
      particle.theta += particle.speed * turb * particle.dir * this.frameScale * (1 + nest * 1.8) * (1 - cloud * 0.85);
      particle.phi += Math.sin(time + particle.twinkle) * 0.0008 * params.turbulence * this.frameScale * (1 - cloud);

      const wobble = Math.sin(time * 3 + particle.twinkle) * params.scatter * 0.3;
      let radius = particle.baseR * params.breathe + wobble;
      if (nest > 0.1) {
        radius *= 0.72 + nest * 0.55 * Math.abs(Math.sin(particle.theta * 2 + time * 3));
      }

      const sinPhi = Math.sin(particle.phi);
      let x = radius * sinPhi * Math.cos(particle.theta);
      let y = radius * sinPhi * Math.sin(particle.theta);
      let z = radius * Math.cos(particle.phi);
      let speakSize = particle.size;
      let speakAlpha = 0.78;
      let speakGlow = params.glow;

      if (cloud > 0.02) {
        const speak = this.speakingPos(particle, time, audio, mood);
        x = lerp(x, speak.x, cloud);
        y = lerp(y, speak.y, cloud);
        z = lerp(z, speak.z, cloud);
        speakSize = speak.size;
        speakAlpha = speak.alpha;
        speakGlow = speak.glow;
      }

      const rx = x * cosR - z * sinR;
      const rz = x * sinR + z * cosR;
      const perspective = 1 / (2.2 - rz);
      const sx = centerX + rx * baseRadius * perspective;
      const sy = centerY + y * baseRadius * perspective;
      const depth = (rz + 1) / 2;
      const sphereAlpha = this.phaseGlow
        ? (0.36 + depth * 1.12) * params.glow * weight * phaseGlow
        : (0.78 + depth * 0.48) * params.glow * weight * (this.crisp ? 1.12 : 1);
      const alpha = Math.min(this.phaseGlow ? 1.2 : 1, lerp(sphereAlpha, speakAlpha * speakGlow * weight * phaseGlow, cloud));
      const sphereSize = particle.size * perspective * crisp * (this.dotScale || 1);
      const cloudSize = speakSize * perspective * crisp * (this.dotScale || 1) * (1 + cloud * 0.18);
      const size = this.dotScale > 1 ? Math.max(0.9, lerp(sphereSize, cloudSize, cloud)) : lerp(sphereSize, cloudSize, cloud);

      context.globalAlpha = alpha;
      context.beginPath();
      context.arc(sx, sy, size, 0, Math.PI * 2);
      context.fill();

      if (depth > 0.62 && cloud < 0.85) {
        context.fillStyle = goldSoft;
        context.globalAlpha = Math.min(1, alpha * (this.crisp ? 0.95 : 0.85) * (1 - cloud));
        context.beginPath();
        context.arc(sx, sy, size * (this.crisp ? 0.3 : 0.42), 0, Math.PI * 2);
        context.fill();
        context.fillStyle = fillGold;
      }
    }
  }

  drawRings(centerX, centerY, baseRadius, params, time, weight) {
    if (weight < 0.02) return;
    const context = this.context;
    const tilt = 0.42 + Math.sin(time * 0.7) * 0.06;
    const rot = time * params.rotation * 18;
    const breathe = params.breathe;

    context.strokeStyle = this.liveGold || this.gold || GOLD;
    context.lineCap = 'round';
    for (let ring = 0; ring < 5; ring += 1) {
      const t = (ring + 1) / 5;
      const radius = baseRadius * (0.26 + t * 0.62) * breathe;
      context.globalAlpha = (0.18 + t * 0.42) * params.glow * weight;
      context.lineWidth = 1.1 + (1 - t) * 2.1;
      context.beginPath();
      context.ellipse(centerX, centerY, radius, radius * tilt, rot * (ring % 2 === 0 ? 1 : -0.65), 0, Math.PI * 2);
      context.stroke();
    }

    context.fillStyle = this.liveGold || this.gold || GOLD;
    for (const particle of this.ringParticles) {
      particle.angle += 0.0045 * particle.dir * (1 + params.turbulence) * this.frameScale;
      const t = (particle.ring + 1) / 5;
      const radius = baseRadius * (0.26 + t * 0.62) * breathe;
      const angle = particle.angle + rot * (particle.ring % 2 === 0 ? 1 : -0.65);
      const rx = Math.cos(angle) * radius;
      const ry = Math.sin(angle) * radius * tilt + particle.jitter * baseRadius;
      const pulse = 0.7 + Math.sin(time * 3 + particle.twinkle) * 0.3;
      context.globalAlpha = (0.35 + t * 0.5) * params.glow * weight * pulse;
      context.beginPath();
      context.arc(centerX + rx, centerY + ry, particle.size, 0, Math.PI * 2);
      context.fill();
    }
  }

  frame(now = performance.now()) {
    const canvas = this.canvas;
    const context = this.context;
    if (!canvas || !context) return;

    const reducedMotion = this.reducedMotion?.matches;
    const frameScale = this.lastFrame ? Math.min((now - this.lastFrame) / (1000 / 60), 2.5) : 1;
    this.lastFrame = now;
    this.frameScale = reducedMotion ? 0 : frameScale;

    const width = this.drawWidth || canvas.clientWidth || 1;
    const height = this.drawHeight || canvas.clientHeight || 1;
    const interactive = this.getAttribute('data-mood-tap') === 'home-cycle';
    const mood = interactive
      ? this.currentMood(now)
      : { glow: 1, breatheAmt: 1, breatheFreq: 1, rotation: 1, turbulence: 1, scatter: 1, energy: 0.55 };
    if (!reducedMotion) {
      this.time += 0.022 * frameScale * (0.82 + (mood.energy ?? 0.55) * 0.4);
    }
    this.liveGold = interactive ? mood.gold : this.gold || GOLD;
    this.liveGoldSoft = interactive ? mood.goldSoft : this.goldSoft || GOLD_SOFT;
    const preset = this.currentPreset(now);
    const params = this.liveParams(preset, this.time, mood);
    const minSide = Math.min(width, height);
    if (this.bloomArmed && this.bloom < 1) {
      this.bloom = Math.min(1, this.bloom + frameScale / (BLOOM_SEC * 60));
    }
    const bloomK = this.bloomEase(this.bloom);
    const radius =
      minSide *
      params.radius *
      (interactive ? this.currentImpulse(now) : 1) *
      (0.12 + 0.88 * bloomK);
    const audio = (params.cloud || 0) * demoSpeakAmp(this.time) * (mood.energy ?? 0.55);

    context.globalCompositeOperation = 'source-over';
    if (this.transparent || reducedMotion) {
      context.clearRect(0, 0, width, height);
    } else {
      const fade = 1 - (1 - (params.trail || TRAIL_FADE)) ** frameScale;
      context.fillStyle = `rgba(0, 0, 0, ${fade})`;
      context.fillRect(0, 0, width, height);
    }

    if (params.cloud > 0.25 || this.phaseGlow) context.globalCompositeOperation = 'lighter';
    this.drawSphere(width / 2, height / 2, radius, params, this.time, params.sphere, mood, audio);
    this.drawRings(width / 2, height / 2, radius, params, this.time, params.rings);
    context.globalCompositeOperation = 'source-over';
    context.globalAlpha = 1;
  }

  start() {
    if (!this.isActive()) return;
    this.resize();
    if (this.frameId) return;
    const tick = (now) => {
      this.frame(now);
      if (this.reducedMotion?.matches) {
        this.frameId = 0;
        return;
      }
      this.frameId = requestAnimationFrame(tick);
    };
    this.frameId = requestAnimationFrame(tick);
  }

  stop() {
    if (this.frameId) cancelAnimationFrame(this.frameId);
    this.frameId = 0;
    this.lastFrame = 0;
  }
}

if (!customElements.get('nara-orb-loading')) {
  customElements.define('nara-orb-loading', NaraOrbLoading);
}
